<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { getExpenses, createExpense, updateExpense, deleteExpense } from '@/api/expenses'
import { getFarms } from '@/api/farmService'
import type { Expense } from '@/types/farmexpenses'
import type { Farm } from '@/types/Farm'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'
import type { PaginatedResponse } from '@/types/api'
import AlertComponent from '@/components/ui/AlertComponent.vue'

// Data
const expenses = ref<Expense[]>([])
const farms = ref<Farm[]>([])
const search = ref<string>('')
const showModal = ref<boolean>(false)
const editMode = ref<boolean>(false)
const form = ref<Partial<Expense>>({ currency: 'TZS' })
const selectedId = ref<string | null>(null)
const currentPage = ref<number>(1)
const itemsPerPage = ref<number>(10)
const selectedFarmForReport = ref<string>('')

  // Alert state
  const showAlert = ref(false)
  const alertTitle = ref('')
  const alertMessage = ref('')
  
// Computed
const filteredExpenses = computed<Expense[]>(() => {
  if (!search.value) return expenses.value

  const searchTerm = search.value.toLowerCase().trim()
  return expenses.value.filter(
    (e) =>
      e.farm_name?.toLowerCase().includes(searchTerm) ||
      e.production_phase?.toLowerCase().includes(searchTerm) ||
      e.description?.toLowerCase().includes(searchTerm) ||
      e.currency?.toLowerCase().includes(searchTerm) ||
      String(e.amount).includes(searchTerm),
  )
})

declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable?: {
      finalY: number
    }
  }
}

const paginatedExpenses = computed<{ index: number; data: Expense }[]>(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return filteredExpenses.value
    .filter(Boolean)
    .slice(start, start + itemsPerPage.value)
    .map((expense, index) => ({
      index: start + index + 1,
      data: expense,
    }))
})

const totalPages = computed<number>(() => {
  return Math.ceil(filteredExpenses.value.length / itemsPerPage.value)
})

// Methods
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

async function fetchExpenses(): Promise<void> {
  try {
    const res = await getExpenses()

    // Handle response as either paginated or plain array
    const data = res.data

    if (Array.isArray(data)) {
      // Non-paginated response
      expenses.value = data.map((expense) => ({
        ...expense,
        amount: Number(expense.amount),
      }))
    } else if (
      data &&
      typeof data === 'object' &&
      Array.isArray((data as PaginatedResponse<Expense>).results)
    ) {
      // Paginated response
      const paginated = data as PaginatedResponse<Expense>
      expenses.value = paginated.results.map((expense) => ({
        ...expense,
        amount: Number(expense.amount),
      }))
    } else {
      console.warn('[fetchExpenses] unexpected response shape', data)
      expenses.value = []
    }
  } catch (error) {
    console.error('Error fetching expenses:', error)
    expenses.value = []
  }
}

async function fetchFarms(): Promise<void> {
  try {
    const res = await getFarms()
    farms.value = res.data.results
  } catch (error) {
    console.error('Error fetching farms:', error)
    farms.value = []
  }
}

async function openCreate(): Promise<void> {
  editMode.value = false
  form.value = { currency: 'TZS' }
  // refresh farms list so the select shows latest registered farms
  await fetchFarms()
  showModal.value = true
}

function openEdit(expense: Expense): void {
  editMode.value = true
  selectedId.value = expense.id
  form.value = { ...expense }
  showModal.value = true
}

async function saveExpense(): Promise<void> {
  try {
    if (editMode.value && selectedId.value && form.value) {
      await updateExpense(selectedId.value, {
        ...form.value,
        amount: Number(form.value.amount),
      })
    } else if (form.value) {
      await createExpense({
        ...form.value,
        amount: Number(form.value.amount),
      })
    }
    await fetchExpenses()
    showModal.value = false
  } catch (error) {
    console.error('Error saving expense:', error)
  }
}

async function removeExpense(id: string): Promise<void> {
  if (confirm('Are you sure you want to delete this expense?')) {
    try {
      await deleteExpense(id)
      await fetchExpenses()
    } catch (error) {
      console.error('Error deleting expense:', error)
    }
  }
}

function generateReport(): void {
  if (!selectedFarmForReport.value) {
    alert('Please select a farm to generate report')
    return
  }

  const farm = farms.value.find((f) => f.id === selectedFarmForReport.value)
  const farmExpenses = expenses.value.filter((e) => e.farm === selectedFarmForReport.value)




  // Function to show alert
  const AlertComponent = (message: string, title: string = 'Alert') => {
    alertTitle.value = title
    alertMessage.value = message
    showAlert.value = true
  }

  // Your existing code
  if (farmExpenses.length === 0) {
    AlertComponent('No expenses found for selected farm', 'Information')
    return
  }

  const doc = new jsPDF()

  // Report Title
  doc.setFontSize(20)
  doc.setTextColor(40, 40, 40)
  doc.text('Farm Management System - Expense Report', 105, 15, { align: 'center' })

  // Farm and Date Information
  doc.setFontSize(14)
  doc.text(`Farm: ${farm?.name || 'Selected Farm'}`, 15, 25)
  doc.text(`Owner: ${farm?.owner_name || 'N/A'}`, 15, 35)
  doc.text(`Report Date: ${formatDate(new Date().toISOString())}`, 15, 45)

  // Table data with row numbers
  const tableData = farmExpenses.map((expense, index) => [
    index + 1,
    expense.farm_name,
    expense.production_phase || 'N/A',
    Number(expense.amount).toLocaleString(),
    expense.currency || 'TZS',
    expense.description || 'N/A',
    expense.created_by_name || 'N/A',
    expense.created_at ? formatDate(expense.created_at) : 'N/A',
  ])

  // Add table
  autoTable(doc, {
    head: [['#', 'Farm', 'Phase', 'Amount', 'Currency', 'Description', 'Created By', 'Date']],
    body: tableData,
    startY: 55,
    theme: 'grid',
    headStyles: {
      fillColor: [34, 139, 34],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 'auto' },
      3: { halign: 'right' },
      4: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' },
    },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  })

  // Calculate and display total
  const finalY = doc.lastAutoTable?.finalY || 55
  const total = farmExpenses.reduce((sum, expense) => sum + Number(expense.amount), 0)

  doc.setFontSize(12)
  doc.setTextColor(40, 40, 40)
  doc.text(
    `Total Expenses: ${total.toLocaleString()} ${farmExpenses[0]?.currency || 'TZS'}`,
    14,
    finalY + 15,
  )

  // Footer
  doc.setFontSize(10)
  doc.setTextColor(100, 100, 100)
  doc.text('Generated by Farm Management System', 105, doc.internal.pageSize.height - 10, {
    align: 'center',
  })

  // Save the PDF
  doc.save(
    `Farm_Expense_Report_${farm?.name.replace(/\s+/g, '_') || 'Farm'}_${new Date().toISOString().slice(0, 10)}.pdf`,
  )
}

// Lifecycle
onMounted((): void => {
  fetchExpenses()
  fetchFarms()
})
</script>

<template>
  <div class="transition-all duration-300">
    <WelcomeBanner />
    <!-- Quick Actions Section -->
    <div class="bg-white rounded-xl shadow-md p-4 mb-6 animate-fade-in">
      <h2 class="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
      <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <button
          @click="openCreate"
          class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow hover:shadow-md transition-all flex items-center gap-2 flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>
          Add Expense
        </button>

        <div class="flex flex-col sm:flex-row gap-2 w-full">
          <select
            v-model="selectedFarmForReport"
            class="bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 flex-grow"
          >
            <option value="" disabled selected>Select Farm for Report</option>
            <option v-for="farm in farms" :key="farm.id" :value="farm.id">
              {{ farm.name }}
            </option>
          </select>
          <button
            @click="generateReport"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow hover:shadow-md transition-all flex items-center gap-2 flex-shrink-0"
            :disabled="!selectedFarmForReport"
            :class="{ 'opacity-50 cursor-not-allowed': !selectedFarmForReport }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            Download Report
          </button>
        </div>
      </div>
    </div>


    <!-- Search & Filter -->
    <div class="mb-6">
      <div class="relative max-w-md">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            ></path>
          </svg>
        </div>
        <input
          v-model="search"
          placeholder="Search by farm name..."
          class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full bg-white bg-opacity-70 backdrop-blur-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="bg-white rounded-xl shadow-md overflow-hidden animate-fade-in">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                #
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Farm
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Phase
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Amount
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Currency
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Description
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="{ index, data: expense } in paginatedExpenses"
              :key="expense?.id ?? index"
              class="hover:bg-gray-50 transition-colors duration-150"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ index }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ expense?.farm_name ?? '—' }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">{{ expense?.production_phase ?? '—' }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  {{ (expense?.amount ?? 0).toLocaleString() }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                >
                  {{ expense?.currency ?? 'TZS' }}
                </span>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-900 max-w-xs truncate">
                  {{ expense?.description ?? '' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="expense && openEdit(expense)"
                  class="text-blue-600 hover:text-blue-900 mr-3 transition-colors"
                >
                  Edit
                </button>
                <button
                  @click="expense?.id && removeExpense(expense.id)"
                  class="text-red-600 hover:text-red-900 transition-colors"
                >
                  Delete
                </button>
              </td>
            </tr>
            <tr v-if="paginatedExpenses.length === 0">
              <td colspan="7" class="px-6 py-4 text-center text-sm text-gray-500">
                No expenses found
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
      >
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="currentPage = currentPage - 1"
            :disabled="currentPage === 1"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            @click="currentPage = currentPage + 1"
            :disabled="currentPage === totalPages"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Next
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-gray-700">
              Showing <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span> to
              <span class="font-medium">{{
                Math.min(currentPage * itemsPerPage, filteredExpenses.length)
              }}</span>
              of <span class="font-medium">{{ filteredExpenses.length }}</span> results
            </p>
          </div>
          <div>
            <nav
              class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                @click="currentPage = currentPage - 1"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
              >
                <span class="sr-only">Previous</span>
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <button
                v-for="page in totalPages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  page === currentPage
                    ? 'bg-green-50 border-green-500 text-green-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                ]"
              >
                {{ page }}
              </button>
              <button
                @click="currentPage = currentPage + 1"
                :disabled="currentPage === totalPages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }"
              >
                <span class="sr-only">Next</span>
                <svg
                  class="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div @click="showModal = false" class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
          >&#8203;</span
        >
        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
              {{ editMode ? 'Edit Expense' : 'Create New Expense' }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Farm</label>
                <select
                  v-model="form.farm"
                  class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md border"
                >
                  <option value="" disabled>Select Farm</option>
                  <option v-for="farm in farms" :key="farm.id" :value="farm.id">
                    {{ farm.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Production Phase</label>
                <input
                  v-model="form.production_phase"
                  placeholder="e.g. Planting, Harvesting"
                  class="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    v-model="form.amount"
                    type="number"
                    placeholder="0.00"
                    class="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Currency</label>
                  <input
                    v-model="form.currency"
                    type="text"
                    placeholder="TZS"
                    maxlength="10"
                    class="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  v-model="form.description"
                  rows="3"
                  class="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md border p-2"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="saveExpense"
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              {{ editMode ? 'Update' : 'Create' }}
            </button>
            <button
              @click="showModal = false"
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <AlertComponent
      v-if="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      @close="showAlert = false"
    />
  </div>
</template>

<style>
/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  table {
    display: block;
    width: 100%;
    overflow-x: auto;
  }
}
</style>
